/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MINE_PAGE_FILTERS_SIDEBAR
 * Date:         2026-01-29
 * Modified By:  Cursor
 *
 * Description:
 * Add filter and pagination state for /broadcast/mine-all filters sidebar.
 * Reuses useBroadcastFilters; same pattern as useBroadcastsList.
 */
/**
 * Phase 7: Fetch "My Broadcasts" only for /broadcast/mine-all page.
 */
import { useState, useEffect, useCallback } from "react";
import { listBroadcastsRaw } from "../../../../services/broadcasts";
import { Broadcast } from "../../../../interfaces/broadcast";
import { getErrorMessage } from "../../../../utils/notifications";
import { useBroadcastFilters } from "../broadcasts-list/hooks/useBroadcastFilters";

export function useBroadcastsMine() {
  const [data, setData] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listBroadcastsRaw({});
      setData(res.items || []);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load broadcasts"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMine();
  }, [fetchMine]);

  // [2026-02-13] UX: Default filters to collapsed for cleaner initial view
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const toggleFilters = useCallback(() => {
    setFiltersCollapsed((prev) => !prev);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);
  const handleItemsPerPageChange = useCallback((newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  }, []);

  const filterState = useBroadcastFilters();

  return {
    data,
    loading,
    error,
    refresh: fetchMine,
    filtersCollapsed,
    toggleFilters,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    ...filterState,
  };
}
